import styled from "styled-components";
import { useState } from "react";
import { X, Check, RotateCcw } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";
import { formatDate } from "./dateFormat";

// Pick which departed line's nights to release when a room has more than one reopen candidate. No
// fetch — candidates come from `props.room.reopen_candidates` (already in the grid payload, ordered
// latest-checkout first). Pick one → confirm → `props.onConfirm(line_id)`.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(2px);
`;

const Panel = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid ${theme.gold};
  box-shadow: 0 24px 60px -18px rgba(26, 26, 26, 0.5);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 1rem 0.6rem;
  border-bottom: 1px solid ${theme.border};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.85rem 1.1rem;
`;

const Intro = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.84rem;
  line-height: 1.5;
  color: ${theme.ink700};
`;

const Item = styled.button`
  display: block;
  width: 100%;
  margin-bottom: 0.45rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid
    ${(props) => (props.$active ? theme.goldDark : theme.border)};
  border-radius: 10px;
  background: ${(props) => (props.$active ? theme.goldSoft : "#ffffff")};
  text-align: left;
  cursor: pointer;
  &:hover {
    border-color: ${theme.gold};
  }
`;

const ItemTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const ItemName = styled.span`
  font-size: 0.95rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const Nights = styled.span`
  flex: 0 0 auto;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid ${theme.gold};
  color: ${theme.goldDark};
  font-size: 0.74rem;
  font-weight: 800;
`;

const ItemSub = styled.div`
  margin-top: 0.2rem;
  font-size: 0.82rem;
  color: ${theme.ink700};
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  padding: 0.7rem 1.1rem;
  border-top: 1px solid ${theme.border};
`;

const Cancel = styled.button`
  padding: 0.45rem 1rem;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  background: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${theme.ink700};
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Confirm = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 1.1rem;
  border: 0;
  border-radius: 9px;
  background: ${theme.ink};
  color: ${theme.gold};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ReopenPickModal = (props) => {
  const { t, lang } = useT();
  const candidates = props.room.reopen_candidates || [];
  const [selected, setSelected] = useState(candidates[0]?.line_id || null);

  return (
    <Overlay onClick={props.onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            <RotateCcw size={16} /> {t("reopen_pick_title")} · {props.room.code}
          </Title>
          <IconButton type="button" onClick={props.onClose} aria-label="close">
            <X size={16} />
          </IconButton>
        </Head>
        <Body>
          <Intro>{t("reopen_pick_intro")}</Intro>
          {candidates.map((candidate) => (
            <Item
              key={candidate.line_id}
              type="button"
              $active={candidate.line_id === selected}
              onClick={() => setSelected(candidate.line_id)}
            >
              <ItemTop>
                <ItemName>{candidate.guest_name || "—"}</ItemName>
                <Nights>
                  {candidate.nights_to_release} {t("reopen_nights")}
                </Nights>
              </ItemTop>
              <ItemSub>
                {formatDate(candidate.check_in_date, lang, false)} →{" "}
                {formatDate(candidate.check_out_date, lang, false)} ·{" "}
                {candidate.status}
              </ItemSub>
            </Item>
          ))}
        </Body>
        <Foot>
          <Cancel type="button" onClick={props.onClose}>
            {t("confirm_cancel")}
          </Cancel>
          <Confirm
            type="button"
            disabled={!selected}
            onClick={() => props.onConfirm(selected)}
          >
            <Check size={15} /> {t("confirm_ok")}
          </Confirm>
        </Foot>
      </Panel>
    </Overlay>
  );
};

export default ReopenPickModal;
